import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { selectComment } from '@redux/actions/mediaViewerAction';
import wait from '@helpers/wait';
import { closeTaskModal, openTaskModal } from '@redux/reducers/tasks';
import {
  DEMO_TOUR_MEDIA_VIEWER_COMMENT_ID,
  DEMO_TOUR_MEDIA_VIEWER_TASK_ID
} from '@config/constants';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';

const event = new MouseEvent('click', {
  bubbles: true,
  cancelable: true,
  view: window
});

export default function useMediaViewerCreateTaskFromComment({
  isAssetReady,
  isPreparing,
  isUnsupported
}: {
  isAssetReady: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
}) {
  const dispatch = useDispatch();
  const { onStartTour } = useOnboardingProcess();
  const isDemoOrganization = useIsDemoOrganization();
  const [searchParams] = useSearchParams();

  const startTour = searchParams.get('startTour');

  const onCloseTask = useCallback(() => {
    dispatch(closeTaskModal());
  }, [dispatch]);

  const onOpenTask = useCallback(async () => {
    dispatch(
      openTaskModal({
        taskId: DEMO_TOUR_MEDIA_VIEWER_TASK_ID,
        descriptionPlaceholder:
          'Describe your task so your teammates know what to do.'
      })
    );
    await wait(2000);
  }, [dispatch]);

  const onOpenCommentMenu = useCallback(async () => {
    const commentHtml = document.querySelector(
      `.media_viewer_comments_container [data-comment-id="${DEMO_TOUR_MEDIA_VIEWER_COMMENT_ID}"] .media_viewer_menu-trigger`
    );
    commentHtml?.dispatchEvent(event);
    await wait(800);
  }, []);

  const onStartTourCreateTaskFromComment = useCallback(() => {
    dispatch(selectComment('new'));
    onStartTour({
      type: 'create-task-from-comment',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 0) {
          await onOpenCommentMenu();
          return;
        }
        if (currentStep === 1) {
          await onOpenTask();
          return;
        }
        if (currentStep === 5) onCloseTask();
      },
      onClickPrev: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 2) {
          await onOpenCommentMenu();
          onCloseTask();
          return;
        }
        if (currentStep === 6) {
          await onOpenTask();
        }
      }
    });
  }, [dispatch, onStartTour, onCloseTask, onOpenTask, onOpenCommentMenu]);

  useEffect(() => {
    if (!isDemoOrganization) return;
    if (startTour !== 'create-task-from-comment') return;
    if (isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTourCreateTaskFromComment();
  }, [
    isDemoOrganization,
    startTour,
    isAssetReady,
    isPreparing,
    isUnsupported,
    onStartTourCreateTaskFromComment
  ]);

  return null;
}
